(function () {
  'use strict';

  var ENGRAVABLE_TAG = 'engravable';
  var PROPERTY_KEY   = 'Engraving Name';

  // ─── Cart API helpers ────────────────────────────────────────────────────────

  function getCart() {
    return fetch('/cart.js').then(function (r) { return r.json(); });
  }

  // Cache product tag lookups so we don't re-fetch the same product twice
  var productTagCache = {};

  function isEngravable(handle) {
    if (productTagCache[handle] !== undefined) {
      return Promise.resolve(productTagCache[handle]);
    }
    return fetch('/products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (product) {
        var result = Array.isArray(product.tags) && product.tags.indexOf(ENGRAVABLE_TAG) !== -1;
        productTagCache[handle] = result;
        return result;
      })
      .catch(function () {
        productTagCache[handle] = false;
        return false;
      });
  }

  function updateLineItem(lineKey, properties) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, properties: properties }),
    }).then(function (r) {
      if (!r.ok) throw new Error('cart update failed');
      return r.json();
    });
  }

  // ─── Build engraving field HTML (pure JS — no Liquid needed) ────────────────

  function buildEngravingFieldHtml(lineKey, existingValue) {
    var escaped = escapeHtml(existingValue || '');
    var hasValue = existingValue && existingValue.trim();

    return '<div class="engraving-field" data-line-key="' + lineKey + '">'
      + '<div class="engraving-toggle">'
      + (hasValue
          ? '<span class="engraving-preview">✏️ Engraving: <strong>' + escaped + '</strong></span>'
            + '<button type="button" class="engraving-edit-btn">Edit</button>'
          : '<button type="button" class="engraving-add-btn">+ Add Engraving</button>'
        )
      + '</div>'
      + '<div class="engraving-input-wrap' + (hasValue ? '' : ' is-hidden') + '">'
      + '<input type="text" class="engraving-input" placeholder="Name to engrave"'
      + ' value="' + escaped + '" maxlength="20" autocomplete="off"'
      + ' data-line-key="' + lineKey + '"'
      + ' aria-label="Engraving name">'
      + '<button type="button" class="engraving-save-btn">Save</button>'
      + '<button type="button" class="engraving-remove-btn"' + (hasValue ? '' : ' style="display:none"') + '>Remove</button>'
      + '</div>'
      + '</div>';
  }

  // ─── Find a cart item's DOM element inside a container ──────────────────────
  // Motion renders items into [data-products]. Each item row commonly has
  // data-line (1-based index), data-key, or data-variant-id. We try all.

  function findItemElement(container, item, index) {
    var el;

    // Most reliable: explicit key attribute
    el = container.querySelector('[data-key="' + item.key + '"]');
    if (el) return el;

    // Some Motion versions use 1-based line number
    el = container.querySelector('[data-line="' + (index + 1) + '"]');
    if (el) return el;

    // Variant-ID attribute (only reliable when no duplicate variants)
    el = container.querySelector('[data-id="' + item.variant_id + '"]');
    if (el) return el;

    // Last resort: nth cart-item row in order
    var rows = container.querySelectorAll(
      '.ajaxcart__row, .cart-item, [data-cart-item], .cart__item'
    );
    if (rows[index]) return rows[index];

    return null;
  }

  // ─── Inject engraving fields into Motion's rendered cart items ───────────────

  function injectFields(container) {
    getCart().then(function (cart) {
      cart.items.forEach(function (item, index) {
        isEngravable(item.handle).then(function (engravable) {
          if (!engravable) return;

          var itemEl = findItemElement(container, item, index);
          if (!itemEl) return;

          // Skip if already injected
          if (itemEl.querySelector('.engraving-field')) return;

          var existingValue = item.properties && item.properties[PROPERTY_KEY]
            ? item.properties[PROPERTY_KEY]
            : '';

          var qtyEl = itemEl.querySelector('.cart__item--qty');
          if (qtyEl) {
            qtyEl.insertAdjacentHTML('beforebegin', buildEngravingFieldHtml(item.key, existingValue));
          } else {
            itemEl.insertAdjacentHTML('beforeend', buildEngravingFieldHtml(item.key, existingValue));
          }

          var field = itemEl.querySelector('.engraving-field');
          if (field) bindField(field);
        });
      });
    }).catch(function () { /* silent — don't break the cart */ });
  }

  // ─── Inline field interaction ────────────────────────────────────────────────

  function setSaving(field, saving) {
    field.classList.toggle('is-saving', saving);
  }

  function showInputWrap(field) {
    var wrap = field.querySelector('.engraving-input-wrap');
    wrap.classList.remove('is-hidden');
    var input = wrap.querySelector('.engraving-input');
    input.focus();
    input.select();
  }

  function hideInputWrap(field) {
    field.querySelector('.engraving-input-wrap').classList.add('is-hidden');
  }

  function setPreview(field, value) {
    var toggle = field.querySelector('.engraving-toggle');
    toggle.innerHTML = '';

    if (value) {
      var preview = document.createElement('span');
      preview.className = 'engraving-preview';
      preview.innerHTML = '✏️ Engraving: <strong>' + escapeHtml(value) + '</strong>';
      toggle.appendChild(preview);

      var editBtn = document.createElement('button');
      editBtn.type = 'button';
      editBtn.className = 'engraving-edit-btn';
      editBtn.textContent = 'Edit';
      editBtn.addEventListener('click', function () { showInputWrap(field); });
      toggle.appendChild(editBtn);

      var removeBtn = field.querySelector('.engraving-remove-btn');
      if (removeBtn) removeBtn.style.display = '';
    } else {
      var addBtn = document.createElement('button');
      addBtn.type = 'button';
      addBtn.className = 'engraving-add-btn';
      addBtn.textContent = '+ Add Engraving';
      addBtn.addEventListener('click', function () { showInputWrap(field); });
      toggle.appendChild(addBtn);

      var removeBtn2 = field.querySelector('.engraving-remove-btn');
      if (removeBtn2) removeBtn2.style.display = 'none';
    }
  }

  function handleSave(field) {
    var input   = field.querySelector('.engraving-input');
    var lineKey = field.dataset.lineKey;
    var value   = input.value.trim();

    if (!value) { input.focus(); return; }

    setSaving(field, true);
    updateLineItem(lineKey, { [PROPERTY_KEY]: value })
      .then(function () {
        setPreview(field, value);
        hideInputWrap(field);
      })
      .catch(function () {
        alert('Could not save engraving — please try again.');
      })
      .finally(function () { setSaving(field, false); });
  }

  function handleRemove(field) {
    var input   = field.querySelector('.engraving-input');
    var lineKey = field.dataset.lineKey;

    setSaving(field, true);
    updateLineItem(lineKey, { [PROPERTY_KEY]: '' })
      .then(function () {
        input.value = '';
        setPreview(field, '');
        hideInputWrap(field);
      })
      .catch(function () {
        alert('Could not remove engraving — please try again.');
      })
      .finally(function () { setSaving(field, false); });
  }

  function bindField(field) {
    if (field.dataset.engravingBound) return;
    field.dataset.engravingBound = '1';

    var addBtn    = field.querySelector('.engraving-add-btn');
    var editBtn   = field.querySelector('.engraving-edit-btn');
    var saveBtn   = field.querySelector('.engraving-save-btn');
    var removeBtn = field.querySelector('.engraving-remove-btn');
    var input     = field.querySelector('.engraving-input');

    if (addBtn)    addBtn.addEventListener('click',    function () { showInputWrap(field); });
    if (editBtn)   editBtn.addEventListener('click',   function () { showInputWrap(field); });
    if (saveBtn)   saveBtn.addEventListener('click',   function () { handleSave(field); });
    if (removeBtn) removeBtn.addEventListener('click', function () { handleRemove(field); });

    if (input) {
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter')  { e.preventDefault(); handleSave(field); }
        if (e.key === 'Escape') { hideInputWrap(field); }
      });
    }
  }

  // ─── Checkout reminder modal ─────────────────────────────────────────────────

  function buildModal(items) {
    var overlay = document.createElement('div');
    overlay.className = 'engraving-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');

    var itemsHtml = items.map(function (item) {
      var title = item.product_title || item.title;
      if (item.variant_title && item.variant_title !== 'Default Title') {
        title += ' – ' + item.variant_title;
      }
      var imgHtml = item.image
        ? '<img class="engraving-modal__item-img" src="' + item.image + '" alt="' + escapeHtml(title) + '" loading="lazy">'
        : '<div class="engraving-modal__item-img-placeholder"></div>';

      var existing = (item.properties && item.properties[PROPERTY_KEY]) ? item.properties[PROPERTY_KEY] : '';

      return '<div class="engraving-modal__item">'
        + imgHtml
        + '<div class="engraving-modal__item-info">'
        + '<p class="engraving-modal__item-title">' + escapeHtml(title) + '</p>'
        + '<input type="text" class="engraving-modal__item-input"'
        + ' placeholder="Name to engrave" maxlength="20"'
        + ' data-line-key="' + item.key + '"'
        + ' value="' + escapeHtml(existing) + '">'
        + '</div>'
        + '</div>';
    }).join('');

    overlay.innerHTML =
      '<div class="engraving-modal">'
      + '<h2 class="engraving-modal__title">Personalise your items</h2>'
      + '<p class="engraving-modal__subtitle">These items can be engraved. Add a name for each one, or skip.</p>'
      + '<div class="engraving-modal__items">' + itemsHtml + '</div>'
      + '<hr class="engraving-modal__divider">'
      + '<div class="engraving-modal__actions">'
      + '<button type="button" class="engraving-modal__confirm-btn">Save &amp; Checkout</button>'
      + '<button type="button" class="engraving-modal__skip-btn">Skip &amp; go to checkout</button>'
      + '</div>'
      + '</div>';

    return overlay;
  }

  function showCheckoutModal(items, onProceed) {
    var overlay = buildModal(items);
    document.body.appendChild(overlay);

    var firstInput = overlay.querySelector('.engraving-modal__item-input');
    if (firstInput) firstInput.focus();

    function proceed() {
      overlay.remove();
      onProceed();
    }

    overlay.querySelector('.engraving-modal__confirm-btn').addEventListener('click', function () {
      var btn     = overlay.querySelector('.engraving-modal__confirm-btn');
      var inputs  = overlay.querySelectorAll('.engraving-modal__item-input');
      var updates = [];

      inputs.forEach(function (input) {
        var value = input.value.trim();
        if (value) updates.push(updateLineItem(input.dataset.lineKey, { [PROPERTY_KEY]: value }));
      });

      btn.disabled    = true;
      btn.textContent = 'Saving…';

      Promise.all(updates).catch(function () {}).finally(proceed);
    });

    overlay.querySelector('.engraving-modal__skip-btn').addEventListener('click', proceed);

    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) proceed();
    });

    function onKeyDown(e) {
      if (e.key === 'Escape') {
        document.removeEventListener('keydown', onKeyDown);
        proceed();
      }
    }
    document.addEventListener('keydown', onKeyDown);
  }

  // ─── Checkout interception ───────────────────────────────────────────────────

  var checkoutInProgress = false;

  function interceptCheckout() {
    document.addEventListener('click', function (e) {
      if (checkoutInProgress) return;

      var trigger = e.target.closest
        ? e.target.closest('[name="checkout"], a[href*="/checkout"], .cart__checkout, .cart__checkout-button')
        : null;

      if (!trigger) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      getCart()
        .then(function (cart) {
          return Promise.all(cart.items.map(function (item) {
            return isEngravable(item.handle).then(function (engravable) {
              var hasValue = item.properties && item.properties[PROPERTY_KEY] && item.properties[PROPERTY_KEY].trim();
              return (engravable && !hasValue) ? item : null;
            });
          }));
        })
        .then(function (results) {
          var needsEngraving = results.filter(Boolean);
          if (needsEngraving.length > 0) {
            showCheckoutModal(needsEngraving, function () {
              checkoutInProgress = true;
              window.location.href = '/checkout';
            });
          } else {
            checkoutInProgress = true;
            window.location.href = '/checkout';
          }
        })
        .catch(function () {
          checkoutInProgress = true;
          window.location.href = '/checkout';
        });
    }, true);
  }

  // ─── Watch [data-products] for Motion's cart re-renders ─────────────────────
  // Motion replaces the entire [data-products] innerHTML on every cart change
  // (add, remove, quantity update). We watch for that and re-inject our fields.

  function observeProductsContainer() {
    var productsEl = document.querySelector('[data-products]');
    if (!productsEl) return;

    var debounceTimer;

    var observer = new MutationObserver(function () {
      // Debounce — Motion may fire multiple mutations in one update cycle
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        injectFields(productsEl);
      }, 80);
    });

    observer.observe(productsEl, { childList: true, subtree: true });

    // Also run immediately in case the drawer already has items rendered
    injectFields(productsEl);
  }

  // ─── Watch for the drawer itself (it may not exist on DOMContentLoaded) ──────

  function waitForDrawer() {
    var drawerEl = document.getElementById('CartDrawer');
    if (drawerEl) {
      observeProductsContainer();
      return;
    }

    // If the drawer HTML isn't in the DOM yet, wait for it
    var bodyObserver = new MutationObserver(function () {
      if (document.getElementById('CartDrawer')) {
        bodyObserver.disconnect();
        observeProductsContainer();
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: false });
  }

  // ─── Utility ─────────────────────────────────────────────────────────────────

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ─── Boot ─────────────────────────────────────────────────────────────────────

  function init() {
    waitForDrawer();
    interceptCheckout();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();